import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Container,
  Heading,
  ProgressBar,
  RESULT_AUTO_ADVANCE_MS,
  ResultBanner,
  Screen,
  Skeleton,
  Stack,
  SubmitBar,
  Text,
} from '@fluent/ui';
import type { Exercise, Lesson } from '@fluent/types';
import { getLesson } from '../content/lessons.js';
import { isPhrasesLessonId } from '../content/course.js';
import { usePhrasesContent } from '../content/usePhrasesContent.js';
import {
  calculateStats,
  initLessonState,
  isRetryPosition,
  lessonReducer,
  playOrder,
  type LessonAction,
  type LessonState,
} from './lessonReducer.js';
import { ExerciseSlot } from './ExerciseSlot.js';
import { useExerciseAnswering } from './useExerciseAnswering.js';
import { exerciseAudioKeys } from './audioKeys.js';
import { lessonExitTo } from './lessonExit.js';
import { WhyCard, WrongTitle, joinTokens, resultDetailNode, whyFor } from './ResultDetail.js';
import { playSfx } from './sounds.js';
import { LessonComplete } from './LessonComplete.js';
import { LevelUpModal } from './LevelUpModal.js';
import { useCompleteLesson } from '../progress/hooks.js';
import { useCelebrationCrossings } from '../progress/useCelebrationCrossings.js';
import { preloadAudio } from '../audio/audioPlayer.js';
import { clearSession, getSession, saveSession } from './sessionStore.js';
import { useCanPlayLesson } from '../subscription/hooks.js';
import { Paywall } from '../subscription/Paywall.js';
import { ReportErrorDialog } from './ReportErrorDialog.js';
import { track } from '../lib/analytics.js';
import './LessonPlayer.css';

/**
 * Module-scoped guard for "this lesson-run already submitted".
 * React's StrictMode mounts → unmounts → remounts, so a local useRef is
 * reset and the completion effect fires twice. Keying on
 * `${lessonId}:${completedAt}` is stable across remounts.
 */
const submittedRuns = new Set<string>();

/**
 * LessonPlayer — the top-level screen at `/learn/:lessonId`. Owns the
 * lesson reducer, the live answer state for each exercise type, and
 * swaps in the completion screen when done.
 *
 * Supports all four exercise types:
 *  - translate_typed → text input, matcher handles typos as "close"
 *  - translate_tap   → tap ordered tokens, exact-sequence check
 *  - listen_type     → audio prompt + text input (audio stubbed until Phase 8)
 *  - tap_pairs       → 2-column match game, auto-completes when all matched
 */
export function LessonPlayer() {
  const { lessonId } = useParams<{ lessonId: string }>();
  // A phrases-course lesson is not in the app's start-up bundle: fetch the
  // phrases lessons first (lessons.ts). Main-course lessons never wait.
  const isPhrases = lessonId ? isPhrasesLessonId(lessonId) : false;
  const phrases = usePhrasesContent(isPhrases);
  const lesson = lessonId ? getLesson(lessonId) : null;
  const canPlay = useCanPlayLesson(lessonId ?? '');

  if (!lesson) {
    if (isPhrases && phrases.status === 'loading') return <LoadingScreen />;
    if (isPhrases && phrases.status === 'error') return <LoadErrorScreen onRetry={phrases.retry} />;
    return <NotFoundScreen />;
  }
  if (canPlay === 'loading') {
    // The subscription read is still in flight. Rendering <Paywall> here
    // sold Pro to people who already own it — and fired `paywall_shown`
    // at them. Wait for the answer; it's one Supabase round-trip.
    return <LoadingScreen />;
  }
  if (canPlay === 'no') {
    return <Paywall fromLessonId={lesson.id} />;
  }
  // Re-opening a lesson (finished or not) drops straight back into the
  // exercises. No read-only recap window on completed lessons — Tornike's
  // call 2026-06-23 ("no need to open this window after completing").
  return <LessonInner lesson={lesson} />;
}

/** The lesson with nothing behind it — no paywall, no saved session, no
 *  completion write, no analytics, no report flag. For the content-review
 *  route on preview builds (LessonReview.tsx), where nobody is signed in.
 *  ✕ and the finish screen go back to `exitTo`. */
export function LessonDemoPlayer({ lesson, exitTo }: { lesson: Lesson; exitTo: string }) {
  return <LessonInner lesson={lesson} demoExitTo={exitTo} />;
}

function LessonInner({ lesson, demoExitTo }: { lesson: Lesson; demoExitTo?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const demo = demoExitTo !== undefined;
  // Where ✕, the finish screen and the level-up modal lead: a phrases-course
  // lesson returns to its own screen (PHRASES.md § 3), everything else Home.
  // The demo player keeps whatever its route asked for.
  const exitTo = demoExitTo ?? lessonExitTo(lesson.id);

  // Analytics — fire `lesson_started` once per mount. The state machine
  // runs inside, so we'd otherwise miss the entry event.
  useEffect(() => {
    if (demo) return;
    track('lesson_started', { lesson_id: lesson.id });
  }, [lesson.id, demo]);

  // Level-up + streak-milestone detection is shared with the ტესტი and
  // flashcard players (progress/useCelebrationCrossings.ts) — the same XP and
  // the same streak, whichever screen earned them.
  // showLevelUp: the modal is actively rendering right now (the crossing
  //              itself is `levelUpPending`, queued until Continue).
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  // The fixed bottom dock (submit bar / result bar) covers the end of the
  // page. A wrong answer's result bar is ~190px tall and the why card sits
  // right above it, so the page needs at least that much room below to
  // scroll the card clear — measured, not guessed (review 2026-10-08).
  const [dockHeight, setDockHeight] = useState(0);
  const dockObserver = useRef<ResizeObserver | null>(null);
  const dockRef = useCallback((el: HTMLDivElement | null) => {
    dockObserver.current?.disconnect();
    dockObserver.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setDockHeight(Math.ceil(el.getBoundingClientRect().height)));
    ro.observe(el);
    dockObserver.current = ro;
  }, []);

  // Hydrate from a saved session (partial-progress resume). The reducer init
  // accepts a Lesson and returns a fresh state — we override with the saved
  // index + results when present so the user picks up where they left off.
  const [state, rawDispatch] = useReducer(
    (s: LessonState, a: LessonAction) => lessonReducer(s, a, lesson),
    lesson,
    (l) => {
      const fresh = initLessonState(l);
      const session = demo ? null : getSession(l.id);
      if (!session) return fresh;
      // Drop the session and start clean when the lesson changed since it was
      // saved: the index is past the end, or the exercises it already closed
      // are no longer at the same places. Lesson 1 went from 25 exercises to
      // 13 (2026-10-06); resuming by index alone would land someone on a test
      // whose phrase_intro they never saw. Starting over costs a minute.
      // Positions past the end are retries of earlier mistakes (playOrder).
      const order = playOrder(l, session.results);
      const changed =
        session.index >= order.length ||
        session.results.some((r, i) => l.exercises[order[i] ?? -1]?.id !== r.exerciseId);
      if (changed) {
        clearSession(l.id);
        return fresh;
      }
      return {
        ...fresh,
        index: session.index,
        results: session.results,
        startedAt: session.startedAt,
      };
    },
  );
  const dispatch = rawDispatch as (a: LessonAction) => void;

  // Watches the XP + streak caches for the crossings the completion RPC
  // causes. Armed only once the run is over, so a background refetch
  // mid-lesson can't fire a celebration.
  const { levelUpPending, streakMilestone, level, clearLevelUp } =
    useCelebrationCrossings(state.phase === 'complete' && !demo);

  // Persist progress on every step. We skip the `checked` mid-state (user is
  // mid-evaluation; resume should land on the next answering exercise) and
  // the `complete` end-state (cleared explicitly below).
  useEffect(() => {
    if (demo) return;
    if (state.phase === 'complete') return; // cleared by the completion effect
    if (state.phase === 'checked') return; // wait for NEXT to land us on answering
    if (state.results.length === 0 && state.index === 0) return; // nothing to save
    // Reviewing a past exercise (index < frontier) must not rewind the saved
    // resume point — persist the frontier, not the review cursor.
    const resumeIndex = Math.max(state.index, state.results.length);
    saveSession({
      lessonId: lesson.id,
      index: resumeIndex,
      results: state.results,
      startedAt: state.startedAt,
      savedAt: Date.now(),
    });
  }, [state.phase, state.index, state.results, state.startedAt, lesson.id, demo]);

  // Play order = the lesson, then one retry of each first-pass mistake.
  const order = useMemo(() => playOrder(lesson, state.results), [lesson, state.results]);
  const at = (position: number): Exercise | undefined => {
    const i = order[position];
    return i === undefined ? undefined : lesson.exercises[i];
  };
  const current: Exercise | undefined = at(state.index);
  const retry = isRetryPosition(lesson, state.index);

  // Answer inputs + grading live in the shared hook — a grading-rule
  // change lands in exactly one place.
  const {
    typedValue,
    setTypedValue,
    tapIndices,
    setTapIndices,
    selectedOption,
    setSelectedOption,
    tapPool,
    choicePool,
    submitCurrent,
    onPairsComplete,
    clearAnswers,
    canSubmit,
  } = useExerciseAnswering({ current, phase: state.phase, dispatch, retry });

  const advance = useCallback(() => {
    clearAnswers();
    dispatch({ type: 'NEXT' });
  }, [clearAnswers, dispatch]);

  const goBack = useCallback(() => {
    clearAnswers();
    dispatch({ type: 'GO_BACK' });
  }, [clearAnswers, dispatch]);

  // Preload the *next* exercise's audio while the user is mid-answer on the
  // current one. Eliminates the cold-fetch wait when they tap "Listen".
  // Every new exercise opens at the top. A why card can scroll a short
  // phone's page down; the next exercise must not start scrolled past its
  // first lines (review 2026-10-08; ExamPlayer does the same).
  useEffect(() => {
    window.scrollTo(0, 0);
    // Again next frame: a why card's smooth scroll still in flight adds one
    // more frame of movement after the first jump.
    const id = requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => cancelAnimationFrame(id);
  }, [state.index]);

  const next = at(state.index + 1);
  useEffect(() => {
    if (!next) return;
    const keys = exerciseAudioKeys(next);
    for (const k of keys) preloadAudio(k);
  }, [next]);

  // Complete SFX + progress-store write fire exactly once per run,
  // driven by the phase flip into `complete`. Dedup via module-scoped
  // Set — survives StrictMode remounts + re-renders from mutation state.
  //
  // `completeLesson.mutate` is stable across renders (React Query), so we
  // intentionally exclude it from the deps list; same for `state` which is
  // replaced by narrower primitives.
  const completeLesson = useCompleteLesson();
  const mutateRef = useRef(completeLesson.mutate);
  mutateRef.current = completeLesson.mutate;
  useEffect(() => {
    if (state.phase !== 'complete' || state.completedAt === null) return;
    const runKey = `${lesson.id}:${state.completedAt}`;
    if (submittedRuns.has(runKey)) return;
    submittedRuns.add(runKey);
    playSfx('complete');
    if (demo) return;
    const stats = calculateStats(state, lesson);
    mutateRef.current({
      lessonId: lesson.id,
      xp: stats.xp,
      accuracy: stats.accuracy,
      perfect: stats.perfect,
    });
    track('lesson_completed', {
      lesson_id: lesson.id,
      xp: stats.xp,
      accuracy: stats.accuracy,
      perfect: stats.perfect,
      first_try_count: stats.firstTryCount,
      total_exercises: stats.totalExercises,
      elapsed_seconds: stats.elapsedSeconds,
    });
    // Lesson finished — wipe the partial-progress session so the home node
    // flips from "in-progress" with partial ring to "complete" / "golden".
    clearSession(lesson.id);
    // Level-up + streak-milestone detection happens in a separate effect
    // that watches the React Query cache for the optimistic update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.completedAt, lesson]);

  if (state.phase === 'complete') {
    const stats = calculateStats(state, lesson);
    // Sequential celebration flow:
    //   1. LessonComplete (stats + streak milestone banner if any)
    //   2. After user taps Continue → if level-up pending → LevelUpModal
    //   3. After LevelUpModal Continue → navigate home
    if (showLevelUp) {
      return (
        <LevelUpModal
          level={level}
          onContinue={() => {
            clearLevelUp();
            navigate(exitTo);
          }}
        />
      );
    }
    return (
      <LessonComplete
        stats={stats}
        streakMilestone={streakMilestone}
        onContinue={() => {
          if (levelUpPending) {
            setShowLevelUp(true); // chain to the LevelUpModal
          } else {
            navigate(exitTo);
          }
        }}
      />
    );
  }

  // Against the play order: a mistake adds its retry, so the bar slows down
  // a little after a wrong answer — it never moves backwards.
  const progress =
    order.length === 0
      ? 0
      : Math.min(
          1,
          (state.index + (state.phase === 'checked' ? 1 : 0)) / order.length,
        );
  const wrong = state.phase === 'checked' && state.lastVerdict === 'wrong';
  const why = wrong ? whyFor(current) : null;
  // What the learner answered — marks the missed words in the reveal.
  const userAnswer =
    current?.type === 'translate_tap' && tapPool
      ? joinTokens(tapIndices.map((i) => tapPool[i] ?? ''))
      : current?.type === 'translate_typed' || current?.type === 'listen_type'
        ? typedValue
        : null;
  // Plain call, not a hook (we are past the early return). A wrong answer's
  // JSX detail is a new object per render — harmless: the bar only re-arms
  // its countdown on correct answers, whose detail is a plain string.
  const detail = resultDetailNode({ t, lastVerdict: state.lastVerdict, current, userAnswer });

  const showSubmitBar = state.phase === 'answering' && current?.type !== 'tap_pairs';
  const submitLabel =
    current?.type === 'phrase_intro' || current?.type === 'dialogue' || current?.type === 'grammar_tip'
      ? t('lesson.intro_continue')
      : t('lesson.submit');

  return (
    <Screen>
      <header className="fluent-lesson__topbar">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(exitTo)}
          aria-label={t('lesson.exit_aria')}
        >
          ✕
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={goBack}
          disabled={state.index === 0}
          aria-label={t('lesson.back_aria')}
          data-testid="lesson-back-btn"
        >
          ←
        </Button>
        <ProgressBar
          value={progress}
          tone="primary"
          size="md"
          label={t('lesson.progress_aria', {
            current: state.index + 1,
            total: order.length,
          })}
        />
        {!demo && <Button
          variant="neutral"
          size="sm"
          onClick={() => setReportOpen(true)}
          aria-label={t('lesson.report_error_aria')}
          data-testid="report-error-flag"
          className="fluent-lesson__report-flag"
        >
          {t('lesson.report_error_label')}
        </Button>}
      </header>

      <ReportErrorDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        lessonId={lesson.id}
        exerciseId={current?.id}
        meta={{
          exercise_type: current?.type,
          last_verdict: state.lastVerdict ?? null,
          phase: state.phase,
        }}
      />

      <Container size="sm">
        <Stack
          gap={6}
          style={{
            padding: `var(--space-6, 24px) 0 ${Math.max(120, dockHeight + 24)}px`,
            ['--fluent-dock-h' as string]: `${dockHeight}px`,
          }}
        >
          {current && retry && (
            <span className="fluent-lesson__retry" data-testid="retry-badge">
              <span aria-hidden="true">↻</span> {t('lesson.retry_badge')}
            </span>
          )}
          {current && (
            // Keyed wrapper — a new play position unmounts + remounts, which
            // re-plays the 200ms fade-in defined in LessonPlayer.css. Keyed on
            // the position too: a retry can follow its own first attempt.
            <div key={`${state.index}:${current.id}`} className="fluent-lesson__exercise-slot">
              <ExerciseSlot
                current={current}
                tapPool={tapPool}
                choicePool={choicePool}
                tapIndices={tapIndices}
                setTapIndices={setTapIndices}
                typedValue={typedValue}
                setTypedValue={setTypedValue}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                onPairsComplete={onPairsComplete}
                onEnter={submitCurrent}
                locked={state.phase !== 'answering'}
                lastVerdict={state.lastVerdict}
              />
            </div>
          )}
          {why && <WhyCard text={why} />}
        </Stack>
      </Container>

      <div className="fluent-lesson__dock" ref={dockRef}>
        {showSubmitBar ? (
          <SubmitBar
            ctaLabel={submitLabel}
            ctaDisabled={!canSubmit}
            onCta={submitCurrent}
          />
        ) : state.phase === 'checked' ? (
          <ResultBanner
            // A second 'close' also lands here, and it counts as correct —
            // only an outright 'wrong' gets the red banner.
            state={state.lastVerdict === 'wrong' ? 'wrong' : 'correct'}
            title={
              state.lastVerdict === 'wrong' ? (
                <WrongTitle text={t('lesson.result.wrong_title')} why={why} />
              ) : (
                t('lesson.result.correct_title')
              )
            }
            detail={detail}
            // A wrong answer waits for the tap: there is a rule or the right
            // sentence to read, and 3 seconds is too short for either.
            autoAdvanceMs={wrong ? undefined : RESULT_AUTO_ADVANCE_MS}
            onCta={advance}
            ctaLabel={t('lesson.result.continue')}
          />
        ) : null}
      </div>
    </Screen>
  );
}

/** Placeholder while the access answer is in flight. */
function LoadingScreen() {
  return (
    <Screen>
      <div className="fluent-lesson__loading" style={{ padding: 'var(--space-6, 24px)' }}>
        <Skeleton shape="card" style={{ height: 280 }} />
      </div>
    </Screen>
  );
}

/** The phrases lessons could not be fetched (offline before they were
 *  cached). Same shape as the exam's load error. */
function LoadErrorScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={4} align="center" style={{ padding: 'var(--space-16, 64px) 0', textAlign: 'center' }}>
          <Heading level={3}>{t('lesson.load_error.title')}</Heading>
          <Text tone="muted">{t('lesson.load_error.body')}</Text>
          <Button variant="primary" onClick={onRetry}>
            {t('lesson.load_error.retry')}
          </Button>
          <Button variant="ghost" onClick={() => navigate('/')}>
            {t('lesson.not_found.home_cta')}
          </Button>
        </Stack>
      </Container>
    </Screen>
  );
}

function NotFoundScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={4} style={{ padding: '64px 0', textAlign: 'center' }}>
          <h1 style={{ fontSize: 'var(--font-size-3xl, 28px)', margin: 0 }}>
            {t('lesson.not_found.title')}
          </h1>
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            {t('lesson.not_found.body')}
          </p>
          <div>
            <Button variant="primary" size="lg" onClick={() => navigate('/')}>
              {t('lesson.not_found.home_cta')}
            </Button>
          </div>
        </Stack>
      </Container>
    </Screen>
  );
}
